import { useState, useEffect, useMemo } from 'react';
import {
	format,
	startOfDay,
	startOfMonth,
	addMonths,
	subMonths,
	isSameDay,
	isSameMonth,
	getDay,
	getDaysInMonth,
} from 'date-fns';
import { publicBookingApi } from '../../api/publicBooking';
import { ApiError } from '../../api/client';
import type { PublicBookingSlot } from '../../types/publicBooking';

const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface BookingDateTimeProps {
	token: string;
	/** Scopes availability to one stylist (paid links). */
	staffId?: number;
	selectedDate: Date | null;
	selectedSlot: PublicBookingSlot | null;
	onDateChange: (date: Date) => void;
	onSlotChange: (slot: PublicBookingSlot | null) => void;
}

/**
 * Month calendar plus the time slots for whichever day is picked.
 *
 * Availability is per-day and comes from the API, so the calendar cannot know
 * ahead of time which future days have openings — every non-past day is
 * selectable and the slot panel reports the truth once the day is chosen.
 */
const BookingDateTime = ({
	token,
	staffId,
	selectedDate,
	selectedSlot,
	onDateChange,
	onSlotChange,
}: BookingDateTimeProps) => {
	const today = useMemo(() => startOfDay(new Date()), []);
	const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
		startOfMonth(selectedDate ?? today)
	);
	const [slots, setSlots] = useState<PublicBookingSlot[]>([]);
	const [isLoadingSlots, setIsLoadingSlots] = useState(false);
	const [slotsError, setSlotsError] = useState<string | null>(null);

	// Load the picked day's slots. `cancelled` guards against a slower earlier
	// request landing after a newer one when the customer clicks around.
	useEffect(() => {
		if (!selectedDate) {
			setSlots([]);
			setSlotsError(null);
			return;
		}

		let cancelled = false;
		const load = async () => {
			setIsLoadingSlots(true);
			setSlotsError(null);
			try {
				const data = await publicBookingApi.getAvailability(
					token,
					format(selectedDate, 'yyyy-MM-dd'),
					staffId
				);
				if (!cancelled) setSlots(data.slots ?? []);
			} catch (error) {
				if (cancelled) return;
				setSlots([]);
				setSlotsError(
					error instanceof ApiError && error.message
						? error.message
						: 'Could not load available times. Please try again.'
				);
			} finally {
				if (!cancelled) setIsLoadingSlots(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [token, staffId, selectedDate]);

	// Blank leading cells so the 1st lands under the right weekday.
	const leadingBlanks = getDay(visibleMonth);
	const daysInMonth = getDaysInMonth(visibleMonth);
	const canGoBack = !isSameMonth(visibleMonth, today) && visibleMonth > today;

	const handleDayClick = (day: number) => {
		const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day);
		onDateChange(date);
		onSlotChange(null);
	};

	return (
		<>
			<div className="pb-dt">
				<div>
					<div className="pb-dt__cal-nav">
						<button
							type="button"
							className="pb-dt__cal-btn"
							onClick={() => setVisibleMonth(month => subMonths(month, 1))}
							disabled={!canGoBack}
							aria-label="Previous month"
						>
							&#8249;
						</button>
						<div className="pb-dt__cal-month">{format(visibleMonth, 'MMMM yyyy')}</div>
						<button
							type="button"
							className="pb-dt__cal-btn"
							onClick={() => setVisibleMonth(month => addMonths(month, 1))}
							aria-label="Next month"
						>
							&#8250;
						</button>
					</div>

					<div className="pb-dt__cal-grid">
						{DOW_LABELS.map(label => (
							<div key={label} className="pb-dt__dow">
								{label}
							</div>
						))}
						{Array.from({ length: leadingBlanks }, (_, index) => (
							<div key={`blank-${index}`} />
						))}
						{Array.from({ length: daysInMonth }, (_, index) => {
							const day = index + 1;
							const date = new Date(
								visibleMonth.getFullYear(),
								visibleMonth.getMonth(),
								day
							);
							const isPast = date < today;
							const isToday = isSameDay(date, today);
							const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
							return (
								<button
									key={day}
									type="button"
									className={`pb-dt__day${isToday ? ' is-today' : ''}${
										isSelected ? ' is-selected' : ''
									}`}
									disabled={isPast}
									onClick={() => handleDayClick(day)}
									aria-label={format(date, 'EEEE, MMMM d, yyyy')}
								>
									{day}
								</button>
							);
						})}
					</div>
				</div>

				<div>
					<div className="pb-dt__slots-label">
						{selectedDate
							? format(selectedDate, 'EEEE, MMMM d, yyyy')
							: 'Select a date to see available times'}
					</div>

					{!selectedDate && (
						<div className="pb-dt__prompt">Pick a date from the calendar.</div>
					)}

					{selectedDate && isLoadingSlots && (
						<div className="pb-dt__prompt">Loading available times…</div>
					)}

					{selectedDate && !isLoadingSlots && slotsError && (
						<div className="pb__error-banner">
							<span>⚠️</span>
							<span>{slotsError}</span>
						</div>
					)}

					{selectedDate && !isLoadingSlots && !slotsError && slots.length === 0 && (
						<div className="pb-dt__prompt">No slots available on this date.</div>
					)}

					{selectedDate && !isLoadingSlots && !slotsError && slots.length > 0 && (
						<div className="pb-dt__slots-grid">
							{slots.map(slot => (
								<button
									key={slot.time}
									type="button"
									className={`pb-dt__slot${
										selectedSlot?.time === slot.time ? ' is-selected' : ''
									}`}
									disabled={slot.busy}
									onClick={() => onSlotChange(slot)}
								>
									{slot.label || slot.time}
								</button>
							))}
						</div>
					)}
				</div>
			</div>

			{selectedDate && selectedSlot && (
				<div className="pb__dt-banner">
					<span>📅</span>
					<span>
						{format(selectedDate, 'EEEE, MMMM d, yyyy')} at{' '}
						{selectedSlot.label || selectedSlot.time}
					</span>
				</div>
			)}
		</>
	);
};

export default BookingDateTime;
